import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { DataGrid } from '@mui/x-data-grid';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, TextField } from '@mui/material';
import { Close, FileDownload, LocationOn } from '@mui/icons-material';
import { DirectionsRenderer, DirectionsService, GoogleMap, LoadScript, Marker, Polyline } from '@react-google-maps/api';
import api from '../config/backend';
import './css/Attendance.css';
import { useParams } from 'react-router-dom';
import DownloadExportData from '../utils/excel';

function Attendence() {
  // Format today's date as YYYY-MM-DD for the date picker
  const pad = (n) => n.toString().padStart(2, '0');
  const today = new Date();
  const todayFormatted = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
  const { status } = useParams();
  const [fromdate, setFromDate] = useState(todayFormatted);
  const [todate, setToDate] = useState(todayFormatted);
  const [rows, setRows] = useState([]);
  const [trackingData, setTrackingData] = useState([]);
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [trackingDate, setTrackingDate] = useState(todayFormatted);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [selectedEmployeeName, setSelectedEmployeeName] = useState('');
  const token = localStorage.getItem("_crm__token");
  const [directions, setDirections] = useState(null);
  const [directionsError, setDirectionsError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'employeeName', headerName: 'Employee Name', width: 180 },
    { field: 'email', headerName: 'Email', width: 220 },
    { field: 'Date', headerName: 'Date', width: 120 },
    { field: 'checkInTime', headerName: 'Check In Time', width: 130 },
    { field: 'checkOutTime', headerName: 'Check Out Time', width: 130 },
    // { field: 'checkInLocation', headerName: 'Check In Location', width: 250 },
    // { field: 'checkOutLocation', headerName: 'Check Out Location', width: 250 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Button
          variant="outlined"
          size="small"
          startIcon={<LocationOn />}
          onClick={() => handleViewTracking(params?.row?.data?.EmployeeId?._id, params?.row?.employeeName, params?.row?.data?.Date)}
          className="tracking-btn"
        >
          Tracking
        </Button>
      ),
    },
  ];

  const fetchAttendance = async () => {
    try {
      const response = await axios.post(`${api}/Employee/getAttendencebymonth`, {
        token,
        startDate: fromdate,
        enddate: todate
      });

      if (response.data.success) {
        // Process attendance data
        const attendanceData = response.data.data;
        const formattedRows = [];

        // For each attendance record, fetch employee details
        for (let i = 0; i < attendanceData.length; i++) {
          const attendance = attendanceData[i];

          formattedRows.push({
            id: i + 1,
            employeeName: attendance?.EmployeeId?.firstName + attendance?.EmployeeId?.lastName || 'Unknown',
            email: attendance?.EmployeeId?.company_email || 'Unknown',
            Date: new Date(attendance?.Date).toLocaleDateString() || '-',
            checkInTime: attendance?.CheckIn_Time || '-',
            checkOutTime: attendance?.CheckOut_Time || '-',
            // checkInLocation: attendance?.CheckIn_location || '-',
            // checkOutLocation: attendance?.CheckOut_location || '-',
            data: attendance,
            // Add any other fields you need
          });
        }

        setRows(formattedRows);
      }
    } catch (error) {
      console.error('Error fetching attendance data:', error);
    }
  };

  const fetchTracking = async (employeeId, date) => {
    try {
      const response = await axios.post(`${api}/Employee/getTrackingbymonth`, {
        token,
        EmployeeId: employeeId,
        startDate: date,
        enddate: date
      });

      if (response.data.success) {
        setTrackingData(response.data.data);
      } else {
        setTrackingData([]);
      }
    } catch (error) {
      console.error('Error fetching tracking data:', error);
      setTrackingData([]);
    }
  };

  useEffect(() => {
    fetchAttendance();
  }, [fromdate]);

  // const handleDateChange = (e) => {
  //   setDate(e.target.value);
  // };

  const handleViewTracking = (employeeId, employeeName, date) => {
    console.log("date", date);
    const today = new Date(date);
    const todayFormatted = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
    setSelectedEmployeeId(employeeId);
    setSelectedEmployeeName(employeeName);
    setTrackingDate(todayFormatted);
    fetchTracking(employeeId, todayFormatted);
    setShowTrackingModal(true);
  };

  const handleTrackingDateChange = (e) => {
    setTrackingDate(e.target.value);
    fetchTracking(selectedEmployeeId, e.target.value);
  };
  // Function to calculate directions
  const calculateDirections = (trackingPoints) => {
    if (!trackingPoints || trackingPoints.length < 2) {
      return;
    }

    // Create origin, destination and waypoints
    const origin = {
      lat: parseFloat(trackingPoints[0].latitude),
      lng: parseFloat(trackingPoints[0].longitude)
    };

    const destination = {
      lat: parseFloat(trackingPoints[trackingPoints.length - 1].latitude),
      lng: parseFloat(trackingPoints[trackingPoints.length - 1].longitude)
    };

    // Create waypoints from the points in between (if any)
    const waypoints = trackingPoints.slice(1, trackingPoints.length - 1).map(point => ({
      location: {
        lat: parseFloat(point.latitude),
        lng: parseFloat(point.longitude)
      },
      stopover: true
    }));

    // Set up the directions request
    const directionsRequest = {
      origin: origin,
      destination: destination,
      waypoints: waypoints,
      travelMode: 'DRIVING'
    };

    // Clear previous directions
    setDirections(null);
    setDirectionsError(null);

    // Return the directions request object
    return directionsRequest;
  };

  // Handle directions response
  const directionsCallback = (response) => {
    if (response !== null) {
      if (response.status === 'OK') {
        setDirections(response);
        setDirectionsError(null);
      } else {
        setDirectionsError('Directions request failed: ' + response.status);
      }
    }
  };

  // Update directions when tracking data changes
  useEffect(() => {
    if (trackingData.length > 0 && trackingData[0]?.Tracking?.length > 1) {
      // Reset directions when tracking data changes
      setDirections(null);
    }
  }, [trackingData]);
  console.log("trackingData", trackingData);

  const handleDownloadExcel = async () => {
    setDownloading(true);
    try {
      const response = await axios.post(`${api}/Employee/getAttendencebymonth`,
        {
          token,
          startDate: fromdate,
          enddate: todate,
          download: true
        },
        {
          responseType: 'blob', // Important for file downloads
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      DownloadExportData(response.data, 'Employee.csv');

    } catch (error) {
      console.error('Error downloading Excel file:', error);
      alert('Failed to download Excel file');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="app-table-container">
      <div className="employee-header">
        <h2>Employee Attendance</h2>
        <div className='Header_div'>
          <Button
            variant="outlined"
            endIcon={<FileDownload />}
            onClick={handleDownloadExcel}
            // className="download-excel-btn"
            disabled={downloading}
          >
            {/* {downloading ? 'Downloading...' : 'Export to Excel'} */}
            <img src='https://cdn-icons-png.flaticon.com/512/888/888850.png' alt='excel_image' width={'30px'} height={'30px'} />
          </Button>
          <TextField
            label="From"
            type="date"
            name="startDate"
            value={fromdate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            fullWidth
            inputProps={{ max: new Date().toISOString().split("T")[0] }}
          />
          <TextField
            label="To"
            type="date"
            name="endDate"
            value={todate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            fullWidth
            inputProps={{
              min: fromdate
                ? new Date(fromdate).toISOString().split("T")[0]
                : undefined,
              max: new Date().toISOString().split("T")[0]
            }}
          />
        </div>
      </div>
      <div className="app-table">
        <DataGrid
          rows={rows}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
          }}
          pageSizeOptions={[5, 10, 20]}
        />
      </div>

      {/* Tracking Modal */}
      <Dialog
        open={showTrackingModal}
        onClose={() => setShowTrackingModal(false)}
        maxWidth="md"
        fullWidth
        className="tracking-modal"
      >
        <DialogTitle className="modal-title">
          <div>
            <h3>Employee Tracking - {selectedEmployeeName}</h3>
            <p className="modal-subtitle">Movement history for the selected date</p>
          </div>
          <IconButton onClick={() => setShowTrackingModal(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <div className="date-picker-container tracking-date-picker">
            <label>Select Date: </label>
            <input
              type="date"
              value={trackingDate}
              onChange={handleTrackingDateChange}
              className="date-picker"
            />
          </div>

          {trackingData.length > 0 ? (
            <div className="map-container">
              <LoadScript googleMapsApiKey="AIzaSyAW-xZdNAskSJteiT9GmKfnFYRqfeZcWkc">
                <GoogleMap
                  mapContainerStyle={{
                    width: '100%',
                    height: '400px'
                  }}
                  center={{
                    lat: parseFloat(trackingData[0]?.latitude || 0),
                    lng: parseFloat(trackingData[0]?.longitude || 0)
                  }}
                  zoom={15}
                >
                  {trackingData.map((record, index) => (
                    // record && record.map((track, index) => (
                    <Marker
                      key={`${record._id}-${index}`}
                      position={{
                        lat: parseFloat(record.latitude),
                        lng: parseFloat(record.longitude)
                      }}
                      label={{
                        text: (index + 1).toString(),
                        color: 'white'
                      }}
                      title={`${record.type} - ${record.location}`}
                    />
                    // ))
                  ))}

                  {trackingData[0] && trackingData[0].length > 1 && !directions && (
                    <DirectionsService
                      options={calculateDirections(trackingData[0])}
                      callback={directionsCallback}
                    />
                  )}

                  {/* Directions Renderer */}
                  {directions && (
                    <DirectionsRenderer
                      options={{
                        directions: directions,
                        suppressMarkers: true, // We already have our own markers
                        polylineOptions: {
                          strokeColor: '#007bff',
                          strokeOpacity: 0.8,
                          strokeWeight: 5,
                        }
                      }}
                    />
                  )}
                </GoogleMap>
              </LoadScript>
              {directionsError && (
                <div className="directions-error">
                  {directionsError}
                </div>
              )}

              <div className="tracking-details">
                <h4>Tracking Details</h4>
                <div className="tracking-table-container">
                  <table className="tracking-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Type</th>
                        <th>Time</th>
                        <th>Location</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trackingData.flatMap((record, index) =>
                        <tr key={`${record._id}-${index}`}>
                          <td>{index + 1}</td>
                          <td>{record.type}</td>
                          <td>
                            {record.Time}
                          </td>
                          <td className="location-cell">{record.location}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <div className="no-data-message">
              No tracking data available for this date.
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTrackingModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default Attendence;
